import React, { useContext, useState } from "react";
import { Modal, Upload, Select, Typography, Space, Button, Form, message } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import { Store } from "../../store";
import { uuid } from "../../utils/helpers";
import { downloadToSystem } from "../../utils/download.utils";
import rackImportTemplate from "../../assets/files/racks-import-excel-template.xlsx";

const { Dragger } = Upload;
const { Text } = Typography;
const { Option } = Select;

const ImportObjectsModal = ({ open, onClose, onImport }) => {
  const { state, dispatch } = useContext(Store);
  const { elements, initialLoad } = state;

  const [excelFile, setExcelFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);
  const [didTemplateStartDownloading, setDidTemplateStartDownloading] = useState(true);

  const handleDownloadSample = () => {
    setDidTemplateStartDownloading(false);
    downloadToSystem({
      fileUrl: rackImportTemplate,
      fileName: "racks-import-template.xlsx",
      successMessage: "Template file downloaded successfully",
      errorMessage: "Failed to download template file",
    }).finally(() => {
      setDidTemplateStartDownloading(true);
    });
  };

  const handleFileUpload = (info) => {
    const file = info.file;
    if (!file) return;
    setExcelFile(file);
    setIsLoadingSheets(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-latest/package/xlsx.mjs");
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetNames = workbook.SheetNames;
      setSheetNames(sheetNames);
      setSelectedSheet(sheetNames[0]);
      const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetNames[0]]);
      setParsedData(jsonData);
      setIsLoadingSheets(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSheetChange = (value) => {
    setSelectedSheet(value);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[value]);
      setParsedData(jsonData);
    };
    reader.readAsArrayBuffer(excelFile);
  };

  const convertExcelToRacks = async (excelData, warehouseLayoutId = 66) => {
    if (!excelData || !Array.isArray(excelData) || excelData.length === 0) {
      console.error("Invalid excelData:", excelData);
      return [];
    }

    const racks = [];
    const paddingY = 10; // Distance between racks vertically
    let currentY = 0;

    // Dynamically import XLSX from CDN
    const XLSX = await import("https://cdn.sheetjs.com/xlsx-latest/package/xlsx.mjs");

    // Read the entire workbook to access all sheets
    const data = await excelFile.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheetNames = workbook.SheetNames;

    for (let index = 0; index < excelData.length; index++) {
      const row = excelData[index];
      const name = row["Rack name"] || row["Rack Name"] || row["rack_name"] || `Rack_${index + 1}`;
      const length = parseFloat(row["Length"] || row["length"]) || 50;
      const width = parseFloat(row["Width"] || row["width"]) || 4;
      const facingDirection = row["Facing direction"] || row["Facing Direction"] || row["facing_direction"] || "EW";
      const binsCount = parseInt(row["Bins count"] || row["Bins Count"] || row["bins_count"]) || 0;
      const levels = parseInt(row["Levels"] || row["levels"]) || 0;
      const columns = parseInt(row["Columns"] || row["columns"]) || 0;
      const uniqueId = `temp_${uuid()}`;

      // Generate rack object
      const rack = {
        code: `${name?.toString().toUpperCase()}_${index + 1}`,
        color: "#ddccaf",
        createdAt: new Date().toISOString(),
        createdBy: null,
        facingDirection,
        id: uniqueId,
        length: length,
        maxCapacity: null,
        name: name.toString(),
        objectType: "rack",
        orientation: null,
        rackView: {
          label: facingDirection,
          rackViewing: ["EAST", "WEST"],
        },
        positionX: 0,
        x1: 0,
        x2: 0 + width,
        y1: currentY,
        y2: currentY + length,
        positionY: currentY,
        subAreas: [],
        unitType: "feet",
        updatedAt: new Date().toISOString(),
        warehouseLayoutId,
        width: width,
        zIndex: 3,
        type: "rectangle",
        bins: [],
        levels: levels,
        columns: columns,
        hasElementChanges: true,
      };

      // Check if binsCount > 0 and if there's a sheet with the rack name
      if (binsCount > 0 && sheetNames.includes(name)) {

        const binSheet = workbook.Sheets[name];
        const binRows = XLSX.utils.sheet_to_json(binSheet);

        // Check if there's at least one data row (excluding header)
        if (binRows.length > 0) {

          const rackBins = generateBinsForRack({
            binRows,
            rack,
            levels,
            columns,
          });

          rack.bins = rackBins;
        } 
      } else if (binsCount > 0) {
        // Add logic if binsCount > 0 but no sheet with the rack name
      }

      racks.push(rack);

      // Update Y position for next rack
      currentY -= length + paddingY;
    }

    return racks;
  };

  // Helper function to generate bins for a specific rack
  const generateBinsForRack = ({ binRows, rack, levels, columns }) => {
    const rackBins = [];

    // Column mapping configuration (similar to your existing code)
    const COLUMN_MAP = {
      BIN_NAME: "Bin Name",
      RACK_CODE: "Rack Code",
      COLUMN_NO: "Column No",
      ROW_NO: "Row No",
      BIN_WIDTH: "Bin Width",
      BIN_HEIGHT: "Bin Height",
      UNIT_TYPE: "Unit Type (inch / feet)",
      MAX_CAPACITY: "Max Capacity",
      LEVEL_LABEL: "Level Label",
      COLUMN_LABEL: "Column Label",
      BAY_LABEL: "Bay Label",
    };

    // Helper function to get value from row with fallback
    const getValue = (row, key, defaultValue = null) => {
      return row[key] !== undefined ? row[key] : defaultValue;
    };

    // Check if Excel has Row No and Column No columns
    const firstRow = binRows[0];
    const hasRowNo = COLUMN_MAP.ROW_NO in firstRow && firstRow[COLUMN_MAP.ROW_NO] !== undefined;
    const hasColumnNo = COLUMN_MAP.COLUMN_NO in firstRow && firstRow[COLUMN_MAP.COLUMN_NO] !== undefined;
    const hasBinWidth = COLUMN_MAP.BIN_WIDTH in firstRow && firstRow[COLUMN_MAP.BIN_WIDTH] !== undefined;
    const hasBinHeight = COLUMN_MAP.BIN_HEIGHT in firstRow && firstRow[COLUMN_MAP.BIN_HEIGHT] !== undefined;

    let maxRow = 0;
    let maxColumn = 0;

    // Determine grid dimensions
    if (hasRowNo && hasColumnNo) {
      // Use values from Excel
      binRows.forEach((binRow) => {
        const row = Number(getValue(binRow, COLUMN_MAP.ROW_NO));
        const column = Number(getValue(binRow, COLUMN_MAP.COLUMN_NO));
        if (row > maxRow) maxRow = row;
        if (column > maxColumn) maxColumn = column;
      });
    } else {
      // Use provided levels and columns
      maxRow = levels;
      maxColumn = columns;

      if (maxRow <= 0 || maxColumn <= 0) {
        console.warn(`Invalid grid dimensions for rack ${rack.name}: ${maxRow} rows × ${maxColumn} columns`);
        return [];
      }
    }

    // Auto-calculate bin dimensions based on rack size
    const rackWidthPx = unitsToPixels(rack.width, rack.unitType);
    const rackHeightPx = unitsToPixels(rack.length, rack.unitType);

    const autoCalculatedWidth = rackWidthPx / maxColumn;
    const autoCalculatedHeight = rackHeightPx / maxRow;

    // Process each bin row
    binRows.forEach((binRow, index) => {
      // Map Excel columns to expected keys
      const binName = getValue(binRow, COLUMN_MAP.BIN_NAME);
      const binWidth = getValue(binRow, COLUMN_MAP.BIN_WIDTH);
      const binHeight = getValue(binRow, COLUMN_MAP.BIN_HEIGHT);
      const unitType = getValue(binRow, COLUMN_MAP.UNIT_TYPE, "feet");
      const maxCapacity = getValue(binRow, COLUMN_MAP.MAX_CAPACITY);

      // Labels from Excel
      const levelLabel = getValue(binRow, COLUMN_MAP.LEVEL_LABEL);
      const columnLabel = getValue(binRow, COLUMN_MAP.COLUMN_LABEL);
      const bayLabel = getValue(binRow, COLUMN_MAP.BAY_LABEL);

      // Calculate row and column numbers
      let rowNo, column;

      if (hasRowNo && hasColumnNo) {
        // Use values from Excel
        rowNo = getValue(binRow, COLUMN_MAP.ROW_NO);
        column = getValue(binRow, COLUMN_MAP.COLUMN_NO);
      } else {
        // Auto-assign row and column based on index
        rowNo = Math.floor(index / maxColumn) + 1;
        column = (index % maxColumn) + 1;
      }

      // Calculate bin dimensions
      let finalWidth, finalHeight;

      if (binWidth && binHeight) {
        // Use dimensions from Excel
        finalWidth = unitsToPixels(Number(binWidth), unitType.toLowerCase());
        finalHeight = unitsToPixels(Number(binHeight), unitType.toLowerCase());
      } else {
        // Use auto-calculated dimensions
        finalWidth = autoCalculatedWidth;
        finalHeight = autoCalculatedHeight;
      }

      const formattedWidth = round(finalWidth);
      const formattedHeight = round(finalHeight);

      // Calculate positions relative to rack
      const internalRow = Number(rowNo) - 1;
      const internalColumn = Number(column) - 1;

      const xPos = internalColumn * formattedWidth;
      const yPos = (internalRow * formattedHeight + formattedHeight) * -1;

      // Build labels array
      const labels = [];
      if (levelLabel) labels.push({ type: "level", name: String(levelLabel) });
      if (columnLabel) labels.push({ type: "column", name: String(columnLabel) });
      if (bayLabel) labels.push({ type: "bay", name: String(bayLabel) });

      const bin = {
        name: binName || `Bin-${rowNo}-${column}`,
        code: binName || `Bin-${rowNo}-${column}`,
        id: `bin_${rack.id}_${internalRow}_${internalColumn}`,
        type: "rectangle",
        objectType: "bin",
        width: formattedWidth,
        height: formattedHeight,
        x1: xPos,
        y1: yPos,
        x2: xPos + formattedWidth,
        y2: yPos + formattedHeight,
        color: "black",
        labels: labels,
        maxCapacity: maxCapacity !== undefined ? parseInt(maxCapacity, 10) : null,
        columnNo: Number(column),
        rowNo: Number(rowNo),
        unitType: unitType,
        rackId: rack.id, // Reference to parent rack
      };

      rackBins.push(bin);
    });

    return rackBins;
  };

  // You'll also need these helper functions (add them if not already available):
  const unitsToPixels = (value, unitType) => {
    // Convert units to pixels - adjust conversion factor as needed
    const conversionFactor = unitType === "feet" ? 10 : 1; // Example conversion
    return value * conversionFactor;
  };

  const round = (value, decimals = 2) => {
    return Number(Math.round(value + "e" + decimals) + "e-" + decimals);
  };

  const handleImport = async () => {
    if (!parsedData.length || !excelFile) {
      message.warning("Please upload Excel file before importing.");
      return;
    }

    try {
      const racks = await convertExcelToRacks(parsedData);

      if (racks.length > 0) {
        dispatch({ type: "SET_ELEMENTS", payload: [...elements, ...racks] });
        message.success(`✅ Successfully imported ${racks.length} racks with bins`);

      } else {
        console.warn("No racks were created from excel data");
        message.warning("No racks were created from the Excel data");
      }
      onClose();
    } catch (error) {
      console.error("Error importing racks:", error);
      message.error("Failed to import racks from Excel");
    }
  };

  return (
    <Modal
      title="Import Warehouse Data"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="import" type="primary" onClick={handleImport} disabled={!parsedData.length}>
          Import
        </Button>,
      ]}
      destroyOnClose
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Excel Upload Section */}
        <div style={{ marginTop: 10 }}>
          <div style={{ marginBottom: 12, textAlign: "center" }}>
            <Text strong>Upload Excel File</Text>
          </div>

          <div style={{ textAlign: "center", marginBottom: 10 }}>
            <Button type="link" icon={<DownloadOutlined />} onClick={handleDownloadSample} style={{ padding: 0 }}>
              {didTemplateStartDownloading ? "Download Template" : "Downloading..."}
            </Button>
          </div>

          <Dragger
            name="file"
            multiple={false}
            accept=".xlsx,.xls"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleFileUpload}
            style={{ borderRadius: 10 }}
          >
            {!excelFile ? (
              <Space direction="vertical" size="small">
                <InboxOutlined style={{ fontSize: 42 }} />
                <Text strong>Drag and drop your file here</Text>
                <Text type="secondary">or click to browse</Text>
                <Text type="success">Only .xlsx files allowed</Text>
              </Space>
            ) : (
              <>
                <InboxOutlined style={{ fontSize: 42 }} />
                <Text>{excelFile.name}</Text>
              </>
            )}
          </Dragger>
        </div>

        {/* Sheet Selection Dropdown */}
        {excelFile && sheetNames.length > 0 && (
          <Form layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Select Sheet">
              <Select value={selectedSheet} onChange={handleSheetChange} loading={isLoadingSheets} placeholder="Select a sheet">
                {sheetNames.map((sheetName) => (
                  <Option key={sheetName} value={sheetName}>
                    {sheetName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </div>
    </Modal>
  );
};

export default ImportObjectsModal;
